/**
 * Sanitize AG-Grid column state that was persisted through the (Python)
 * backend. That serializer writes JSON `null` as the literal STRING "None",
 * so saved state arrives with `flex: "None"`, `pinned: "None"`,
 * `sort: "None"`, `aggFunc: "None"`, etc.
 *
 * Feeding `flex: "None"` (a string) into `api.applyColumnState` makes AG Grid's
 * width engine compute NaN and relayout forever — the page goes unresponsive.
 * The other invalid string values (pinned/sort/aggFunc) cause similar grief.
 *
 * This coerces every "None" to null and validates the typed fields so the
 * state is safe to apply. Use it everywhere saved column state is restored
 * (layout manager, Set-Table-State button, etc.).
 */
export const sanitizeSavedColumnState = (state) =>
  (Array.isArray(state) ? state : []).map((cs) => {
    const out = {};
    for (const [k, v] of Object.entries(cs || {})) {
      out[k] = v === "None" ? null : v;
    }
    out.flex = typeof out.flex === "number" ? out.flex : null;
    if (typeof out.width !== "number") delete out.width;
    out.pinned =
      out.pinned === "left" || out.pinned === "right" ? out.pinned : null;
    out.sort = out.sort === "asc" || out.sort === "desc" ? out.sort : null;
    out.sortIndex = typeof out.sortIndex === "number" ? out.sortIndex : null;
    out.rowGroupIndex =
      typeof out.rowGroupIndex === "number" ? out.rowGroupIndex : null;
    out.pivotIndex = typeof out.pivotIndex === "number" ? out.pivotIndex : null;
    out.aggFunc =
      typeof out.aggFunc === "string" && out.aggFunc ? out.aggFunc : null;
    out.rowGroup = Boolean(out.rowGroup);
    out.pivot = Boolean(out.pivot);
    out.hide = Boolean(out.hide);
    return out;
  });

export default sanitizeSavedColumnState;
